import {
  PageBlueprint,
  createFrontendPlugin,
  createRouteRef,
} from '@backstage/frontend-plugin-api';
import { FormFieldBlueprint } from '@backstage/plugin-scaffolder-react/alpha';
import DescriptionIcon from '@material-ui/icons/Description';

/**
 * Single route ref shared by every content page. The blueprint registers
 * the path pattern `/p/:slug`; React Router does the per-slug match at
 * runtime inside `DynamicContentPage`.
 *
 * We don't expose this routeRef to other plugins — there's no
 * "navigate to /p/foo" entry point outside of the sidebar items
 * we render in `Sidebar.tsx`, which use the plain `to={href}` string
 * from the entity annotation.
 */
const contentPageRouteRef = createRouteRef();

/**
 * One catch-all `PageBlueprint` that handles every `/p/:slug`. See the
 * long header comment on `DynamicContentPage.tsx` for why we chose a
 * wildcard route instead of one route per entity.
 *
 * `noHeader: true` tells our `BoschPageLayout` to skip its own banner
 * for this route — the loader renders its own `<Header />` from
 * `core-components` (Bosch-blue via the `BackstageHeader` style
 * overrides) using the title/subtitle pulled from the entity.
 *
 * `nav-item:content-pages` is taken in `Sidebar.tsx` so the framework
 * doesn't auto-emit a generic "/p" nav entry — we render per-entity
 * sidebar items dynamically instead.
 */
const contentPageRoutePage = PageBlueprint.make({
  params: {
    path: '/p/:slug',
    title: 'Content Page',
    icon: <DescriptionIcon />,
    routeRef: contentPageRouteRef,
    noHeader: true,
    loader: () =>
      import('../../components/contentPages/DynamicContentPage').then(m => (
        <m.DynamicContentPage />
      )),
  },
});

/**
 * Custom Scaffolder field — the `WidgetGallery`. Lets admins pick,
 * configure, reorder (DnD), and preview widgets when creating a new
 * content page through `/create > New portal page`.
 *
 * The dynamic import keeps the gallery, the preview dialog, the icon
 * picker, and `react-beautiful-dnd` OUT of the main app bundle — they
 * only load when someone actually opens the Scaffolder template.
 *
 * Naming note: the registry key `widget-gallery` is internal to the
 * Backstage extension system. What the template YAML references is
 * the `name` declared inside `createFormField` ("WidgetGallery"), via
 * `ui:field: WidgetGallery`. Keep them in sync mentally; the only
 * one that's user-visible is the createFormField name.
 */
const widgetGalleryFormField = FormFieldBlueprint.make({
  name: 'widget-gallery',
  params: {
    field: () =>
      import('../../components/contentPages/WidgetGallery').then(
        m => m.WidgetGalleryFormField,
      ),
  },
});

/**
 * Registered as a frontend plugin (not a module) so the auto-derived
 * nav item integrates cleanly with `nav.take(...)` / `nav.rest(...)`
 * in `Sidebar.tsx` — same pattern `kindsModule`, `catalogModule`, etc.
 * follow. The pluginId derives the extension id (`page:content-pages`),
 * which `Sidebar.tsx` takes explicitly so the catch-all `/p/:slug`
 * doesn't pollute the alphabetical "rest" group.
 *
 * The plugin contributes TWO extensions: the `/p/:slug` page renderer
 * and the `WidgetGallery` Scaffolder field. Hosting both here keeps
 * everything content-page-related in one module — adding a new field
 * (e.g. an `edit-content-page` template's "page picker") is a single
 * `FormFieldBlueprint.make(...)` call appended to `extensions` below.
 */
export const contentPagesModule = createFrontendPlugin({
  pluginId: 'content-pages',
  routes: {
    contentPage: contentPageRouteRef,
  },
  extensions: [contentPageRoutePage, widgetGalleryFormField],
});
